
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminSettings } from "@/types/adminSettings";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Info } from "lucide-react";

interface TestingModeTabProps {
  settings: AdminSettings;
  updateSettings: (path: string[], value: any) => void;
  saveSettings: () => Promise<void>;
  isSaving: boolean;
}

export const TestingModeTab: React.FC<TestingModeTabProps> = ({ 
  settings,
  updateSettings,
  saveSettings,
  isSaving
}) => {
  const saveTestingMode = async () => {
    // Save to localStorage immediately for quicker feedback
    try {
      const adminSettings = localStorage.getItem('ADMIN_SETTINGS') || '{}';
      const parsedSettings = JSON.parse(adminSettings);
      parsedSettings.testing = {
        ...parsedSettings.testing,
        enabled: settings.testing.enabled
      };
      localStorage.setItem('ADMIN_SETTINGS', JSON.stringify(parsedSettings));
      console.log("Testing mode settings saved to localStorage:", settings.testing.enabled);
    } catch (error) {
      console.error("Error saving testing mode to localStorage:", error);
    }
    
    // Then save to the database
    await saveSettings();
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Testing Mode</CardTitle>
        <CardDescription>
          Configure settings for testing the application
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {settings.testing.enabled && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Testing Mode Enabled</AlertTitle>
            <AlertDescription>
              In testing mode, you'll see a test email viewer on the Auth page that shows verification 
              and password reset emails. This helps you test without requiring real email delivery.
              All verification steps will also be recorded to the database.
            </AlertDescription>
          </Alert>
        )}
        
        {!settings.testing.enabled && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Testing Mode Disabled</AlertTitle>
            <AlertDescription>
              Testing mode is currently disabled. Verification and password reset emails will be sent to 
              real email addresses, and verification steps will NOT be recorded to the database.
            </AlertDescription>
          </Alert>
        )}
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="testing-mode">Testing Mode</Label>
              <p className="text-sm text-muted-foreground">
                Enables email preview for verification and password resets
              </p>
            </div>
            <Switch 
              id="testing-mode"
              checked={settings.testing.enabled}
              onCheckedChange={(checked) => updateSettings(['testing', 'enabled'], checked)}
            />
          </div>

          {settings.testing.enabled && (
            <div className="space-y-2">
              <Label htmlFor="test-email">Testing Email Address</Label>
              <p className="text-sm text-muted-foreground mb-2">
                The email address to use for testing - you'll see verification and reset emails for this address
              </p>
              <Input
                id="test-email"
                type="email"
                value={settings.email.test}
                onChange={(e) => updateSettings(['email', 'test'], e.target.value)}
                placeholder="test@example.com"
              />
            </div>
          )}

          <div className="pt-4 border-t">
            <div className="mb-4">
              <h3 className="text-md font-medium mb-2">What does Testing Mode do?</h3>
              <div className="text-sm text-muted-foreground space-y-2">
                <p>
                  When testing mode is enabled:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>A test email viewer appears on the Auth page</li>
                  <li>Verification emails are captured and displayed in the viewer</li>
                  <li>Password reset emails are captured and displayed in the viewer</li>
                  <li>Verification steps are recorded to the database</li>
                  <li>You can test the full auth flow without sending real emails</li>
                </ul>
                <p className="mt-2">
                  <Info className="h-4 w-4 inline mr-1" />
                  This is especially useful during development and testing phases.
                </p>
              </div>
            </div>
          </div>
        </div>

        <Button 
          onClick={saveTestingMode} 
          disabled={isSaving}
          className="mt-4"
        >
          {isSaving ? "Saving..." : "Save Settings"}
        </Button>
      </CardContent>
    </Card>
  );
};
